import Signature from './AbstractSignature';
import { CreateElement, VNode } from 'vue';
import { ChildParams, referencesMethod } from '@/templates/References';

export default function KotlinSignatureFactory(kind: string) {
  switch (kind) {
    case 'method':
      return KotlinMethodSignature;
    case 'prop':
      return KotlinPropertySignature;
    case 'constructor':
      return KotlinMethodSignature;
    default:
      return {
        getSignature(_method: any): null {
          return null;
        },
      };
  }
}

class KotlinMethodSignature extends Signature {
  public static LANG = 'kotlin';

  public static getSignature(method: referencesMethod): (h: CreateElement) => VNode {
    const returnsType = this.returnsType(method.returns);
    const paramSignatures = this.paramSignatures(method.params);
    const annotations = this.getAnnotations(method.annotations);

    const isComposable = method.annotations?.includes('Composable');
    const isConstructor = method.kind?.includes('constructor');

    return (h) =>
      h('code', [
        ...annotations(h),
        h('span', { class: 'token function' }, isConstructor ? '' : 'fun '),
        h('span', { class: 'token function' }, `${method.title}`),
        h('span', { class: 'token punctuation' }, isComposable ? '(\n' : '('),
        paramSignatures(h, isComposable),
        h('span', { class: 'token punctuation' }, isComposable ? '\n)' : ')'),
        h('span', { class: 'token punctuation' }, isConstructor ? '' : ': '),
        returnsType(h),
      ]);
  }

  private static getAnnotations(annotations: string[]): (h: CreateElement) => VNode[] {
    return (h) => {
      return (
        annotations?.map((text) => {
          return h('div', { class: 'token annotation' }, `@${text}`);
        }) || []
      );
    };
  }

  private static paramSignatures(
    paramSignatures: ChildParams[]
  ): (h: CreateElement, multiline: boolean) => VNode {
    if (!paramSignatures) return (h) => h();

    return (h, multiline) => {
      const params = h(
        'span',
        paramSignatures.map((val: ChildParams, idx: number) => {
          return h('span', [
            multiline ? `    ${val.title}` : val.title,
            h('span', { class: 'token punctuation' }, ': '),

            val.types ? this.returnsType(val.types)(h) : '',

            !(idx === paramSignatures.length - 1)
              ? h('span', { class: 'token punctuation' }, multiline ? `, \n` : ', ')
              : '',
          ]);
        })
      );
      return params;
    };
  }
}

class KotlinPropertySignature extends Signature {
  public static LANG = 'kotlin';

  public static getSignature(property: any): (h: CreateElement) => VNode {
    const isReadonly = property.attributes?.readonly;
    const returnsType = this.returnsType(property.returns);
    return (h) =>
      h('code', [
        h('span', { class: 'token function' }, isReadonly ? 'val ' : 'var '),
        `${property.title}`,
        h('span', { class: 'token punctuation' }, ': '),
        returnsType(h),
      ]);
  }
}
